/**
 * BookCarouselSection Component
 * 
 * Horizontal scrollable section with book cards
 */

import React from 'react';
import { View, StyleSheet, ScrollView, Pressable, ActivityIndicator } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme, BookCard } from '../../../components/ui';
import { FireBook } from '../../../models/FirestoreModels';

interface BookCarouselSectionProps {
  title: string;
  subtitle?: string;
  books: FireBook[];
  loading?: boolean;
  onSeeAll?: () => void;
  seeAllText?: string;
  icon?: keyof typeof Ionicons.glyphMap;
}

export const BookCarouselSection: React.FC<BookCarouselSectionProps> = ({
  title,
  subtitle,
  books,
  loading = false,
  onSeeAll,
  seeAllText = 'See all',
  icon,
}) => {
  const navigation = useNavigation();

  const handleBookPress = (book: FireBook) => {
    const bookId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
    // @ts-ignore - Navigation types
    navigation.navigate('BookDetail', {
      bookId,
      languageCode: book.languageCode || 'en',
    });
  };

  if (loading) {
    return (
      <View style={styles.container}>
      <View style={styles.header}>
        <View>
          <View style={styles.titleRow}>
            {icon && (
              <Ionicons name={icon} size={20} color={Theme.colors.primary} />
            )}
            <Text variant="h3" style={styles.title}>{title}</Text>
          </View>
          {subtitle && (
            <Text variant="caption" style={styles.subtitle}>
              {subtitle}
            </Text>
          )}
        </View>
      </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={Theme.colors.primary} />
        </View>
      </View>
    );
  }

  if (books.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <View style={styles.titleRow}>
            {icon && (
              <Ionicons name={icon} size={20} color={Theme.colors.primary} />
            )}
            <Text variant="h3" style={styles.title}>{title}</Text>
          </View>
          {subtitle && (
            <Text variant="caption" style={styles.subtitle}>
              {subtitle}
            </Text>
          )}
        </View>
        {onSeeAll && (
          <Pressable onPress={onSeeAll} hitSlop={8}>
            <Text variant="body2" style={styles.seeAllText}>
              {seeAllText} →
            </Text>
          </Pressable>
        )}
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {books.map((book, index) => (
          <BookCard
            key={`${book.id}-${index}`}
            title={book.title}
            author={book.author}
            coverUrl={book.coverImageUrl || book.coverImageFullUrl}
            insightsCount={book.insightIds?.length || 0}
            variant="carousel"
            onPress={() => handleBookPress(book)}
            style={index === 0 ? styles.firstCard : undefined}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 40,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.md,
  },
  headerText: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
  },
  title: {
    color: Theme.colors.sectionHeader,
  },
  subtitle: {
    marginTop: 2,
    color: Theme.colors.textSecondary,
  },
  seeAllText: {
    color: Theme.colors.primary,
    fontWeight: '600',
  },
  scrollContent: {
    paddingHorizontal: Theme.spacing.lg,
    gap: Theme.spacing.md,
  },
  firstCard: {
    marginLeft: 0,
  },
  loadingContainer: {
    height: 180,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

